import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Zap, Check, ArrowRight, Star, BadgeCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { relativeTime } from "@/lib/format";

const LEVEL = {
  HIGH: { color: "#F87171", bg: "#F8717118", label: "HIGH IMPACT" },
  MEDIUM: { color: "#FB923C", bg: "#FB923C18", label: "MEDIUM" },
  LOW: { color: "#64748B", bg: "#64748B18", label: "LOW" },
};

function ChangeRow({ c, showWhy }) {
  const lv = LEVEL[c.level] || LEVEL.LOW;
  const transition = c.from_status
    ? <><span className="text-slate-400">{c.from_status}</span> <ArrowRight size={11} className="inline text-slate-500" /> <span className="font-semibold" style={{ color: lv.color }}>{c.to_status}</span></>
    : <span className="font-semibold" style={{ color: lv.color }}>{c.to_status}</span>;
  return (
    <div data-testid={`change-row-${c.id}`} className="flex items-start gap-3 py-3 border-b border-[#1A2133] last:border-0">
      <div className="flex flex-col items-center shrink-0 w-14">
        <span className="text-lg font-heading font-extrabold leading-none" style={{ color: lv.color }}>{c.impact}</span>
        <span className="text-[8px] font-mono uppercase tracking-wider mt-0.5" style={{ color: lv.color }}>{c.level}</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={c.player_id ? `/players/${c.player_id}` : "/feed"} className="text-sm font-semibold text-slate-100 hover:text-[#00E5A3] truncate">{c.player_name}</Link>
          {c.position && <span className="text-[10px] font-mono text-slate-500">{c.position}</span>}
          {c.affects_watchlist && (
            <span data-testid={`change-watchlist-${c.id}`} className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded text-[#00E5A3] bg-[#00E5A3]/10 flex items-center gap-1">
              <Star size={9} /> On your watchlist
            </span>
          )}
          {(c.sources || []).length > 1 && (
            <span data-testid={`change-confirmed-${c.id}`} className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded text-[#38BDF8] bg-[#38BDF8]/12 flex items-center gap-1" title={`Reported by ${c.sources.map((s) => s.name).join(", ")}`}>
              <BadgeCheck size={9} /> Confirmed by {c.sources.length} sources
            </span>
          )}
        </div>
        <div className="text-[13px] mt-0.5">{transition}</div>
        <div className="flex items-center gap-2 mt-1 text-[10px] font-mono text-slate-500 flex-wrap">
          <span>{c.league}{c.team ? ` · ${c.team}` : ""}</span>
          <span>· {relativeTime(c.ts)}</span>
          {(c.sources || (c.source ? [{ name: c.source, url: c.source_url }] : [])).map((s, i) => (
            <span key={i} data-testid={`change-source-${c.id}`} className="px-1.5 py-0.5 rounded bg-[#38BDF8]/10 text-[#38BDF8] uppercase tracking-wide" title={`Sourced from ${s.name}`}>
              {s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:underline">{s.name}</a> : s.name}
            </span>
          ))}
        </div>
        {showWhy && (c.factors || []).length > 0 && (
          <ul data-testid={`change-why-${c.id}`} className="mt-2 space-y-1 border-l-2 border-[#232D42] pl-3">
            {c.factors.map((f, i) => (
              <li key={i} className="text-[11px] text-slate-400 leading-snug">
                <span className="text-slate-200 font-semibold">{f.label}:</span> {f.note}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function WhatChanged() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortMode, setSortMode] = useState("impact");
  const [showWhy, setShowWhy] = useState(localStorage.getItem("sq_show_why") === "on");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (user) {
        const r = await api.get("/changes/summary");
        setData({ ...r.data, mode: "since" });
      } else {
        const since = localStorage.getItem("sq_changes_since");
        const r = await api.get(`/changes?limit=12${since ? `&since=${encodeURIComponent(since)}` : ""}`);
        setData({ changes: r.data.changes, new_count: r.data.count, affecting_you: 0, mode: "guest" });
      }
    } catch { setData({ changes: [] }); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const markSeen = async () => {
    if (user) { await api.post("/changes/seen"); } 
    localStorage.setItem("sq_changes_since", new Date().toISOString());
    load();
  };

  if (loading || !data) return null;
  const raw = data.changes || [];
  if (raw.length === 0) return null;
  const affecting = data.affecting_you || 0;

  const toggleWhy = () => {
    const next = !showWhy;
    setShowWhy(next);
    localStorage.setItem("sq_show_why", next ? "on" : "off");
  };

  const changes = [...raw].sort((a, b) =>
    sortMode === "recent"
      ? (b.ts || "").localeCompare(a.ts || "")
      : (Number(b.affects_watchlist) - Number(a.affects_watchlist)) || (b.impact - a.impact)
  );

  return (
    <div data-testid="what-changed-hero" className="rounded-2xl bg-[#0B0F18] border border-[#232D42] overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-b border-[#232D42] bg-gradient-to-r from-[#0F1A16] to-transparent">
        <div className="flex items-center gap-2 min-w-0">
          <Zap size={18} className="text-[#00E5A3] shrink-0" />
          <div className="min-w-0">
            <h2 className="font-heading font-bold text-slate-100 text-base sm:text-lg leading-tight">
              What changed{user ? " since your last visit" : ""}
            </h2>
            <p className="text-[11px] text-slate-500">
              {data.new_count} update{data.new_count === 1 ? "" : "s"}
              {affecting > 0 && <span className="text-[#00E5A3]"> · {affecting} on your watchlist</span>}
            </p>
          </div>
        </div>
        <button data-testid="what-changed-seen" onClick={markSeen}
          className="text-[11px] font-mono text-slate-400 hover:text-[#00E5A3] flex items-center gap-1 shrink-0">
          <Check size={13} /> Mark seen
        </button>
      </div>

      <div className="flex items-center justify-between gap-2 px-4 sm:px-5 py-2 border-b border-[#232D42] flex-wrap">
        <div className="flex items-center gap-1 bg-[#0F1420] rounded-lg p-0.5">
          <button data-testid="sort-impact" onClick={() => setSortMode("impact")}
            className={`text-[11px] font-mono px-2.5 py-1 rounded-md transition-colors ${sortMode === "impact" ? "bg-[#38BDF8] text-[#04140F]" : "text-slate-400"}`}>By impact</button>
          <button data-testid="sort-recent" onClick={() => setSortMode("recent")}
            className={`text-[11px] font-mono px-2.5 py-1 rounded-md transition-colors ${sortMode === "recent" ? "bg-[#38BDF8] text-[#04140F]" : "text-slate-400"}`}>Most recent</button>
        </div>
        <button data-testid="toggle-why" onClick={toggleWhy}
          className={`text-[11px] font-mono px-2.5 py-1 rounded-lg border transition-colors ${showWhy ? "text-[#00E5A3] border-[#00E5A3]/50 bg-[#00E5A3]/10" : "text-slate-400 border-[#232D42]"}`}>
          {showWhy ? "Hide why" : "Show why it matters"}
        </button>
      </div>

      {affecting > 0 && (
        <div data-testid="what-changed-nudge" className="px-4 sm:px-5 py-2.5 bg-[#00E5A3]/8 border-b border-[#00E5A3]/20 flex items-center justify-between gap-3">
          <span className="text-[12px] text-slate-200">
            <strong className="text-[#00E5A3]">{affecting}</strong> of these hit players you follow — check what it touches.
          </span>
          <Link to="/watchlist" data-testid="what-changed-watchlist-link" className="text-[11px] font-mono text-[#00E5A3] hover:underline flex items-center gap-1 shrink-0">
            My watchlist <ArrowRight size={12} />
          </Link>
        </div>
      )}

      <div className="px-4 sm:px-5">
        {changes.slice(0, 8).map((c) => <ChangeRow key={c.id} c={c} showWhy={showWhy} />)}
      </div>
    </div>
  );
}
